import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { MessageBoxService } from 'app/shared/messagebox';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { TelemetryService } from 'app/core/services/telemetry.service';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad {
  /**
   * Constructor
   */
  constructor(
    private authService: AuthService,
    private router: Router,
    private messageBox: MessageBoxService,
    private telemetryService: TelemetryService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.check(route);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.check(childRoute);
  }

  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.check(route);
  }

  private check(route: ActivatedRouteSnapshot | Route): Observable<boolean> {
    // Check the neccessary roles
    const roles: string[] = route.data['roles'];
    if (!roles || !roles.length || roles.some((role) => this.authService.hasRole(role))) {
      return of(true);
    }

    // Navigate back to the current URL, so we don't remain stuck on a deep link
    // The current URL is root in case of a deep link
    this.router.navigateByUrl(this.router.url);

    const message =
      "You don't have permission to perform this action.\n\nPlease contact your administrator if you received this message in error.";

    this.telemetryService.logExceptionMessage(
      'router',
      'access permission denied for:' + this.router.url.substring(0, 250),
      SeverityLevel.Warning
    );

    return this.messageBox.showMessage('Security', message, ['Ok']).pipe(map(() => false));
  }
}
